import { DayOfWeek, LocalDate, TemporalAdjusters } from "@js-joda/core";
import { useEffect, useMemo, useState } from "react";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { isOk } from "utils/result";
import {
  formatIsoDate,
  getStockholmLocalDateNow,
} from "web-apps/company/components/schedule/utils/schedule.dates";
import { ScheduleSortBy } from "services/api/company/schedule.service";
import type { UnitTeamOption } from "../toolbar/unitTeamDropdown.component";
import type {
  CompanyWorker,
  OrganizationalStructureListItem,
  PersonOption,
} from "../../utils/schedule.utils.types";
import {
  loadCompanyWorkers,
  loadOrganizationalStructureList,
} from "../../utils/schedule.utils.loaders";

export type GroupByOption = "individual" | "team";
export type IntervalOption = "weekly" | "monthly";
export type SizeOption = "large" | "small";

export interface UseScheduleToolbarFiltersState {
  groupByOption: GroupByOption;
  intervalOption: IntervalOption;
  sizeOption: SizeOption;
  searchValue: string;
  searchTags: string[];
  weekDate: LocalDate;
  monthDate: LocalDate;
  range: { fromDate: string; toDate: string };
  pageSize: number;
  sortBy: ScheduleSortBy;
  workGroupIds: number[];
  workerIds: number[];
  unitTeamOptions: UnitTeamOption[];
  selectedWorkGroupIds: string[];
  personOptions: PersonOption[];
  selectedPersonIds: string[];
  setGroupByOption: (value: GroupByOption) => void;
  setIntervalOption: (value: IntervalOption) => void;
  setSizeOption: (value: SizeOption) => void;
  setSearchValue: (value: string) => void;
  setSearchTags: (value: string[]) => void;
  setWeekDate: (value: LocalDate) => void;
  setMonthDate: (value: LocalDate) => void;
  setSelectedWorkGroupIds: (values: string[]) => void;
  setSelectedPersonIds: (values: string[]) => void;
}

const PAGE_SIZE = 50;

/**
 * Builds the active date range based on interval and selected dates.
 */
const buildRange = (
  intervalOption: IntervalOption,
  weekDate: LocalDate,
  monthDate: LocalDate,
): { fromDate: string; toDate: string } => {
  if (intervalOption === "weekly") {
    const start = weekDate.with(
      TemporalAdjusters.previousOrSame(DayOfWeek.MONDAY),
    );
    const end = start.plusDays(6);
    return {
      fromDate: formatIsoDate(start),
      toDate: formatIsoDate(end),
    };
  }

  const start = monthDate.withDayOfMonth(1);
  const end = monthDate.withDayOfMonth(monthDate.lengthOfMonth());
  return {
    fromDate: formatIsoDate(start),
    toDate: formatIsoDate(end),
  };
};

/**
 * Builds unit/team select options from the organizational structure list.
 */
const buildUnitTeamOptions = (
  items: OrganizationalStructureListItem[],
): UnitTeamOption[] => {
  const units: UnitTeamOption[] = [];
  const unitIndex = new Map<number, UnitTeamOption>();
  items.forEach((item) => {
    let unit = unitIndex.get(item.companyUnitId);
    if (!unit) {
      unit = {
        unitId: item.companyUnitId,
        unitName: item.companyUnitName,
        teams: [],
      };
      unitIndex.set(item.companyUnitId, unit);
      units.push(unit);
    }
    if (!unit.teams.some((team) => team.id === item.workGroupId)) {
      unit.teams.push({
        id: item.workGroupId,
        name: item.workGroupName,
      });
    }
  });
  return units;
};

/**
 * Maps company workers to select-friendly person options.
 */
const toPersonOptions = (workers: CompanyWorker[]): PersonOption[] => {
  return workers.map((worker) => {
    const label = `${worker.firstName ?? ""} ${worker.lastName ?? ""}`.trim();
    return {
      value: String(worker.id),
      label: label.length > 0 ? label : String(worker.id),
    };
  });
};

/**
 * Schedule toolbar filter hook that manages range, grouping, and size options.
 */
export const useScheduleToolbarFilters = (): UseScheduleToolbarFiltersState => {
  const [companyState] = useCompanyStore();
  const companyId = companyState.company?.id;

  const [groupByOption, setGroupByOption] =
    useState<GroupByOption>("individual");
  const [intervalOption, setIntervalOption] =
    useState<IntervalOption>("weekly");
  const [sizeOption, setSizeOption] = useState<SizeOption>("large");
  const [searchValue, setSearchValue] = useState("");
  const [searchTags, setSearchTags] = useState<string[]>([]);
  const [weekDate, setWeekDate] = useState(getStockholmLocalDateNow());
  const [monthDate, setMonthDate] = useState(getStockholmLocalDateNow());
  const [personOptions, setPersonOptions] = useState<PersonOption[]>([]);
  const [unitTeamOptions, setUnitTeamOptions] = useState<UnitTeamOption[]>([]);
  const [selectedWorkGroupIds, setSelectedWorkGroupIds] = useState<string[]>(
    [],
  );
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([]);

  const range = useMemo(
    () => buildRange(intervalOption, weekDate, monthDate),
    [intervalOption, monthDate, weekDate],
  );

  const workGroupIds = useMemo(() => {
    return selectedWorkGroupIds
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value));
  }, [selectedWorkGroupIds]);

  const workerIds = useMemo(() => {
    return selectedPersonIds
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value));
  }, [selectedPersonIds]);

  const sortBy = useMemo(
    () => (groupByOption === "team" ? "workGroup" : "worker"),
    [groupByOption],
  );

  useEffect(() => {
    if (!companyId) {
      setPersonOptions([]);
      return;
    }
    let isMounted = true;
    loadCompanyWorkers(companyId).then((result) => {
      if (!isMounted) return;
      if (isOk(result)) {
        setPersonOptions(toPersonOptions(result.value));
        return;
      }
      setPersonOptions([]);
    });
    return () => {
      isMounted = false;
    };
  }, [companyId]);

  useEffect(() => {
    if (!companyId) {
      setUnitTeamOptions([]);
      return;
    }
    let isMounted = true;
    loadOrganizationalStructureList(companyId).then((result) => {
      if (!isMounted) return;
      if (isOk(result)) {
        setUnitTeamOptions(buildUnitTeamOptions(result.value));
        return;
      }
      setUnitTeamOptions([]);
    });
    return () => {
      isMounted = false;
    };
  }, [companyId]);

  useEffect(() => {
    if (unitTeamOptions.length === 0) return;
    const availableIds = new Set(
      unitTeamOptions.flatMap((unit) =>
        unit.teams.map((team) => String(team.id)),
      ),
    );
    setSelectedWorkGroupIds((prev) =>
      prev.filter((value) => availableIds.has(value)),
    );
  }, [unitTeamOptions]);

  return {
    groupByOption,
    intervalOption,
    sizeOption,
    searchValue,
    searchTags,
    weekDate,
    monthDate,
    range,
    pageSize: PAGE_SIZE,
    sortBy,
    workGroupIds,
    workerIds,
    unitTeamOptions,
    selectedWorkGroupIds,
    personOptions,
    selectedPersonIds,
    setGroupByOption,
    setIntervalOption,
    setSizeOption,
    setSearchValue,
    setSearchTags,
    setWeekDate,
    setMonthDate,
    setSelectedWorkGroupIds,
    setSelectedPersonIds,
  };
};
